import { FunctionComponent, useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Box, Typography } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import clsx from 'clsx';
import { MarketplacePetTag, MarketplacePetTags } from '@spike/marketplace-model';
import { ConfirmDialog } from 'components/UI';
import { useNonInitialEffect } from '@versiondos/hooks';
import { getPetTagsThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { BusinessSettingsTitle } from '../UI';
import { reduceResolution, wbp } from 'Theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showError } from '@spike/notifications-action';
import { useApiClientWrapper, useMarketplace } from 'hooks';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Tabs, { positiveTabId, negativeTabId } from './Tabs';
import PetTagTab from './PetTagTab';

export interface Props {
    personalities?: Array<string>;
    onSavePositivePersonality?: (personalities: Array<string>) => void;
    onSaveNegativePersonality?: (personalities: Array<string>) => void;
    className?: string;
    openSidebar?: () => void;
    isMobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            [theme.breakpoints.down(wbp)]: {
                width: `${reduceResolution(565)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                width: '565px'
            },
            [theme.breakpoints.down('sm')]: {
                width: '100%',
                height: '100%'
            }
        },
        titleContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            marginBottom: '-20px'
        },
        businessSettingsTitle: {
            'display': 'flex',
            'alignItems': 'center',
            '& $iconBusinessTitle': {
                fontSize: '16px'
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: '20px',
                marginBottom: '5px'
            }
        },
        iconBusinessTitle: {
            position: 'relative',
            marginRight: '8px'
        },
        subtitle: {
            width: '100%',
            fontSize: `$16px`,
            [theme.breakpoints.down(wbp)]: {
                lineHeight: `${reduceResolution(35)}px`,
                marginBottom: `${reduceResolution(10)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                lineHeight: '42px',
                marginBottom: '10px'
            }
        },

        taxContainer: {
            display: 'flex',
            width: '100%',
            [theme.breakpoints.down(wbp)]: {
                marginTop: `${reduceResolution(44)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '44px'
            }
        },
        taxContainerFirst: {
            display: 'flex',
            width: '100%'
        },
        taxName: {
            display: 'flex',
            width: '65%',
            color: '#D3D3D3',

            [theme.breakpoints.down(wbp)]: {
                paddingRight: `${reduceResolution(19)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingRight: '19px'
            }
        },
        taxNameField: {
            '& input': {
                [theme.breakpoints.down(wbp)]: {
                    paddingLeft: `${reduceResolution(30)}px`
                },
                [theme.breakpoints.up(wbp)]: {
                    paddingLeft: '30px'
                }
            },
            '& .MuiOutlinedInput-root': {
                [theme.breakpoints.down(wbp)]: {
                    borderRadius: `${reduceResolution(33)}px`
                },
                [theme.breakpoints.up(wbp)]: {
                    borderRadius: '33px'
                },
                '& .MuiOutlinedInput-input:-webkit-autofill': {}
            },
            '& fieldset': {
                border: '1px #D3D3D3 solid',
                borderColor: '#D3D3D3 !important'
            },
            '& input.focus-visible ~ fieldset': {
                border: '1px solid #222222 !important'
            }
        },
        taxValue: {
            display: 'flex',
            width: '22.5%',
            [theme.breakpoints.down('sm')]: {
                width: '37%'
            }
        },
        taxValueField: {
            'textAlign': 'right',
            '& .MuiOutlinedInput-adornedEnd': {
                '& .MuiInputBase-input': {
                    fontWeight: 500
                },
                '& p': {
                    color: '#7A7A7A'
                },
                '& input': {
                    [theme.breakpoints.down(wbp)]: {
                        paddingLeft: `${reduceResolution(30)}px`
                    },
                    [theme.breakpoints.up(wbp)]: {
                        paddingLeft: '30px'
                    }
                },
                '& fieldset': {
                    border: '1px #D3D3D3 solid',
                    borderColor: '#D3D3D3 !important'
                },
                '& input.focus-visible ~ fieldset': {
                    border: '1px solid #222222 !important'
                }
            }
        },
        trashIconBox: {
            display: 'flex',
            width: '10%'
        },
        trashIcon: {
            fontSize: `$16px`,
            cursor: 'pointer',
            color: 'black',
            opacity: 1,
            marginTop: 'auto',
            marginBottom: 'auto',
            paddingLeft: '18px',
            paddingTop: '20px'
        },

        trashIconDisabled: {
            cursor: 'default',
            opacity: 0.7
        },

        addOtherContainer: {
            marginTop: '40px',
            [theme.breakpoints.down(wbp)]: {
                width: `${reduceResolution(600)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                width: '600px'
            }
        },

        addOther: {
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: `$16px`,
            [theme.breakpoints.down(wbp)]: {
                lineHeight: `${reduceResolution(30)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                lineHeight: '30px'
            },
            [theme.breakpoints.down('sm')]: {
                color: '#92B4A7'
            }
        },
        addOtherDisabled: {
            cursor: 'default',
            color: '#666666'
        },
        buttonContainer: {
            display: 'flex',
            width: '100%',

            [theme.breakpoints.down(wbp)]: {
                marginTop: `${reduceResolution(28)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '28px'
            },
            [theme.breakpoints.down('sm')]: {
                position: 'absolute',
                bottom: 0
            }
        },
        right: {
            justifyContent: 'flex-end'
        },
        lineDivider: {
            borderWidth: '1px',
            width: 'inherit',
            marginTop: '18px',
            marginBottom: '-25px',
            borderColor: '#efefef69',
            [theme.breakpoints.up('md')]: {
                visibility: 'hidden'
            }
        },
        button: {
            [theme.breakpoints.down('sm')]: {
                width: '100%',
                height: '55px',
                borderRadius: '30px',
                marginRight: '30px',
                marginBottom: '20px'
            }
        },
        description: {
            lineHeight: '1.4',
            fontSize: '16px',
            marginTop: '-15px',
            marginBottom: '35px',

            [theme.breakpoints.down(wbp)]: {
                fontSize: '14px',
                width: '100%'
            },
            [theme.breakpoints.up(wbp)]: {
                width: '840px'
            }
        }
    })
);

export const Personalities: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const marketplace = useMarketplace();

    const loading = useSelector<RootState, boolean>(state => state.marketplace.loading);
    const status = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);
    const store = useSelector<RootState, MarketplacePetTags>(state => state.marketplace.petTags);

    const [newTagName, setNewTagName] = useState('');
    const [editingTag, setEditingTag] = useState<string | null>(null);
    const [petTagSelected, setPetTagSelected] = useState<MarketplacePetTag | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const [positives, setPositives] = useState<MarketplacePetTag[]>([]);
    const [negatives, setNegatives] = useState<MarketplacePetTag[]>([]);
    const [marketplacePositives, setMarketplacePositives] = useState([...marketplace.petPositivePersonality]);
    const [marketplaceNegatives, setMarketplaceNegatives] = useState([...marketplace.petNegativePersonality]);

    const [selectedTab, setSelectedTab] = useState<string>(positiveTabId);

    const dispatch = useDispatch();

    const apiClientWrapper = useApiClientWrapper();

    useEffect(() => {
        dispatch(getPetTagsThunk(apiClientWrapper));
    }, [dispatch]);

    useNonInitialEffect(() => {
        if (status === MarketplaceStatus.SaveSuccess) {
            dispatch(getPetTagsThunk(apiClientWrapper));
            setNewTagName('');
            setShowForm(false);
            setShowModalDelete(false);
            setEditingTag('');
            setPetTagSelected(null);
            setMarketplacePositives(marketplace.petPositivePersonality);
            setMarketplaceNegatives(marketplace.petNegativePersonality);
        }

        if (status === MarketplaceStatus.GetPetTagsSuccess) {
            setPositives(sortedPetTags(store.positive));
            setNegatives(sortedPetTags(store.negative));

            setMarketplacePositives(marketplace.petPositivePersonality);
            setMarketplaceNegatives(marketplace.petNegativePersonality);
        }
    }, [status]);

    const sortedPetTags = (petTagList: MarketplacePetTag[]) => {
        const getSourceOrder = (source: string) => {
            return source === 'default' ? 0 : 1;
        };

        return [...petTagList].sort((a, b) => {
            const sourceOrderA = getSourceOrder(a.source);
            const sourceOrderB = getSourceOrder(b.source);

            if (sourceOrderA !== sourceOrderB) {
                return sourceOrderA - sourceOrderB;
            }

            return a.name.localeCompare(b.name);
        });
    };

    const addOtherHandle = () => {
        setShowForm(true);
    };

    const editHandle = (petTag: MarketplacePetTag) => {
        setNewTagName('');
        setShowForm(true);
        setNewTagName(petTag.name);
        setEditingTag(petTag.id);
    };

    const openModalDelete = (petTag: MarketplacePetTag) => {
        setShowModalDelete(true);
        setPetTagSelected(petTag);
    };

    const cancelHandle = () => {
        setShowForm(false);
        setNewTagName('');
        setEditingTag(null);
    };

    const cancelModalHandle = () => {
        setShowModalDelete(false);
    };

    const deletePetTagHandle = () => {
        if (!petTagSelected) {
            return;
        }

        const isPositiveTab = selectedTab === positiveTabId;
        const currentList = isPositiveTab ? marketplacePositives : marketplaceNegatives;
        const onSavePersonality = isPositiveTab ? props.onSavePositivePersonality : props.onSaveNegativePersonality;

        const newTags = currentList.filter(tag => tag !== petTagSelected.id);

        onSavePersonality?.(newTags);
    };

    const areFieldsValid = (name: string, petTagsDefault: MarketplacePetTag[]) => {
        const nameLower = name.toLowerCase();
        const nameWithUnderscore = nameLower.replace(/'/g, '').replace(/ /g, '_');
        const nameWithHyphen = nameLower.replace(/'/g, '').replace(/ /g, '-');

        const tagExists = petTagsDefault.some(tag => {
            const tagIdLower = tag.id.toLowerCase();
            return tagIdLower === nameWithUnderscore || tagIdLower === nameWithHyphen;
        });

        return tagExists;
    };

    const cleanInput = (value: string): string => {
        return value.replace(/[^a-zA-Z0-9\s]/g, '');
    };

    const addPetTagHandle = (name: string, editingTag: string | null) => {
        let newValue = name;

        newValue = cleanInput(newValue);
        setNewTagName(newValue);

        if (newValue.trim() === '') {
            return;
        }

        const isPositiveTab = selectedTab === positiveTabId;
        const currentList = isPositiveTab ? marketplacePositives : marketplaceNegatives;
        const currentListAll = isPositiveTab ? positives : negatives;
        const onSavePersonality = isPositiveTab ? props.onSavePositivePersonality : props.onSaveNegativePersonality;

        if (areFieldsValid(newValue, currentListAll)) {
            dispatch(showError('This personality is already added'));
            return;
        }

        let newTags: string[];

        if (editingTag) {
            newTags = currentList.filter(tag => tag !== editingTag).concat(newValue);
        } else {
            newTags = [...currentList, newValue];
        }

        onSavePersonality?.(newTags);
    };

    const selectTabHandler = (tabId: string) => {
        setSelectedTab(tabId);
        setShowForm(false);
        setNewTagName('');
        setEditingTag(null);
    };

    const description = "Add the pet tags that you want to set for the clients' pets.";

    const personalitiesToShow = selectedTab === positiveTabId ? positives : negatives;

    const activeTabId = selectedTab === positiveTabId ? positiveTabId : negativeTabId;

    return (
        <Box className={clsx(classes.container, props.className)}>
            <Box className={classes.titleContainer}>
                <BusinessSettingsTitle className={classes.businessSettingsTitle}>
                    {' '}
                    {props.isMobile && (
                        <FontAwesomeIcon
                            className={classes.iconBusinessTitle}
                            onClick={props.openSidebar}
                            icon={faArrowLeft}
                            size="2x"
                        />
                    )}{' '}
                    Pet Tags
                </BusinessSettingsTitle>
                <Typography className={classes.description}>{description}</Typography>
            </Box>

            <Tabs
                tabId={selectedTab}
                selectTabHandler={selectTabHandler}
            />
            {(selectedTab === positiveTabId || selectedTab === negativeTabId) && (
                <PetTagTab
                    tabId={activeTabId}
                    loading={loading}
                    showForm={showForm}
                    newTagName={newTagName}
                    editingTag={editingTag}
                    personalities={personalitiesToShow}
                    setNewTagName={setNewTagName}
                    openModalDelete={openModalDelete}
                    addOtherHandle={addOtherHandle}
                    addPetTagHandle={addPetTagHandle}
                    editPetTagHandle={editHandle}
                    addCancelHandle={cancelHandle}
                />
            )}
            {showModalDelete && petTagSelected && (
                <ConfirmDialog
                    open={true}
                    title={<Typography>Delete {petTagSelected.name}</Typography>}
                    question={
                        <Typography>
                            This personality is assigned to <strong>{petTagSelected.count}</strong> pets. <br />
                            Are you sure you want to delete it?
                        </Typography>
                    }
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Delete"
                    onCancel={cancelModalHandle}
                    onConfirm={() => deletePetTagHandle()}
                    processing={loading}
                />
            )}
        </Box>
    );
};

export default Personalities;
